import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, first, map } from 'rxjs';
import { FeatureAccessService } from './feature-access.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureAccessGuard implements CanActivate {
  hasRedirected = false;

  constructor(
    private router: Router,
    private featureAccessService: FeatureAccessService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): Observable<boolean> | Promise<boolean> | boolean {
    const url = state.url;

    return this.featureAccessService.checkAccess().pipe(
      first(), 
      map((permission: any) => {
        if (['WaitingForApproval', 'WaitingForVerification', 'PasswordCreated', 'WaitingForSelfEkyc'].includes(permission?.status)) {
          this.router.navigate(['./']);
          return false;
        } 
        if (permission?.resetRequired === 'Yes') {
          this.router.navigate(['./change-password']);
          return false;
        }
        if(this.checkMenuAccess(permission?.menuJson, url)){
          this.router.navigate(['./']);
          return false;
        }
        return true;
      })
    );
}

private checkMenuAccess(menuJson: any, url: string): boolean {
  if (!menuJson || !url) {
    return false;
  }

  const routeMap = {
    "My Accounts": "/my-accounts",
    "atMyBank": "/transfer/at-my-bank",
    "beftnToAccount": "/transfer/beftn",
    "beftnToCard": "/transfer/beftnCard",
    "npsbToAccount": "/transfer/otherBankAccount",
    "npsbToCard": "/transfer/otherBankCard",
    "transferBeneficiaryManagement": "/transfer/beneficiary-management",
    "commonPendingTransactions": "/transfer/pending-list",
    "bKashTransfer": "/bkash-add-money",
    "bKashBeneficiaryManagement": "/bkash-beneficiary-management",
    "bKashPendingTransactions": "/bkash-pending-transaction",
    "nagadTransfer": "/add-nagad-money",
    "nagadBeneficiaryManagement": "/add-nagad-beneficiary",
    "nagadPendingTransactions": "/pending-transaction",
    "mobileRechargeTransfer": "/mobile-recharge",
    "mobileRechargeBeneficiaryManagement": "/recharge-beneficiary",
    "mobileRechargePendingTransactions": "/recharge-pending-transaction",
    "summary": "/inquiry/summary",
    "statement": "/inquiry/statement",
    "miniStatement": "/inquiry/mini-statement",
    "transferHistory": "/inquiry/transfer-history",
    "branchLocator": "/branch-locator",
    "branchLocatorMap": "/branch-locator-map",
    "emiCalculator": "/emi-calculator",
    "advanceLoanCalculator": "/advance-loan-calculator",
    "electricityBill": "/bills/electricity-bill",
    "waterBill": "/bills/water-bill",
    "gasBill": "/bills/gas-bill",
    "pending": "/bills/pending",
    "history": "/bills/history",
    "payFee": "/pay-fee",
    "educationFeeHistory": "/education-fee-history",
    "pendingPaymentHistory": "/pending-payment-history",
    "positivePayInstruction": "/cheque-service/positive-pay",
    "requestNewChequeBook": "/cheque-service/request-new-cheque-book",
    "manageChequeBookRequests": "/cheque-service/cheque-book-requests",
    "stopChequeRequest": "/cheque-service/stop-cheque-request",
    "stopChequeRequestHistory": "/cheque-service/stop-cheque-requests",
    "standingInstructions": "/standing-instructions"
  };

  return menuJson.some(menuItem => {
    if (!menuItem.enabled) {
      return menuItem.leftMenuId.some(leftMenuItem => routeMap[leftMenuItem.key] && url.includes(routeMap[leftMenuItem.key]));
    } else {
      const topMenuMatch = routeMap[menuItem.topMenuId] && url.includes(routeMap[menuItem.topMenuId]) && !menuItem.enabled;
      const leftMenuMatch = menuItem.leftMenuId.some(leftMenuItem => routeMap[leftMenuItem.key] && url.includes(routeMap[leftMenuItem.key]) && !leftMenuItem.enabled);
      return topMenuMatch || leftMenuMatch;
    }
  });
}

}
